import Decimal from 'decimal.js';
import { freeTimeStatus, missingTradeDocs, type TradeDirection, type TradeDocKind } from './trade';

/**
 * Lô hàng XNK – nhánh D (DEC-550, docs/phases/P10/xnk-lo-hang.md): cảnh báo lô, đối chiếu chứng từ AI đọc với lô / đơn mua,
 * hồ sơ VAT 0% lô xuất, nhãn in PI / Invoice / Packing List song ngữ. Hàm thuần, server và web dùng chung.
 */

/** Dung sai số lượng / tiền khi đối chiếu Invoice, PL với đơn mua (DEC-006: ±3%). */
export const TRADE_DOC_TOLERANCE_PCT = 3;
/** Còn ≤ N ngày tới ETA (nhập) / ETD (xuất) mà thiếu chứng từ thì báo. */
export const TRADE_DOCS_WARN_DAYS = 7;
/** Còn ≤ N ngày hết free time thì báo vàng, quá hạn báo đỏ. */
export const TRADE_FREE_TIME_WARN_DAYS = 2;

export const TRADE_ALERT_KINDS = ['eta_slip', 'eta_overdue', 'docs_missing', 'free_time', 'not_in_asia', 'payment_overdue'] as const;
export type TradeAlertKind = (typeof TRADE_ALERT_KINDS)[number];
export const TRADE_ALERT_LABELS: Record<TradeAlertKind, string> = {
  eta_slip: 'ETA lùi',
  eta_overdue: 'Quá ETA chưa đến',
  docs_missing: 'Sắp đến / sắp chạy, thiếu chứng từ',
  free_time: 'Sắp hết / quá free time cont',
  not_in_asia: 'Về kho chưa nhập Asia',
  payment_overdue: 'Quá hạn thu / trả tiền',
};

export interface TradeAlert {
  kind: TradeAlertKind;
  level: 'warning' | 'error';
  text: string;
}

/** Chứng từ phải có trước khi tàu đến (nhập) / tàu chạy (xuất). Tờ khai, chứng từ thanh toán làm sau nên không tính. */
export const TRADE_DOCS_BEFORE_MOVE: Record<TradeDirection, readonly TradeDocKind[]> = {
  import: ['contract', 'commercial_invoice', 'packing_list', 'bill_of_lading'],
  export: ['contract', 'commercial_invoice', 'packing_list'],
};

export interface ShipmentAlertInput {
  direction: TradeDirection;
  status: string;
  /** ETA hiện tại */
  eta: string | null;
  /** ETA lần đầu (ngày dự kiến của mốc "Tàu đến"), để biết ETA lùi bao nhiêu ngày */
  etaBaseline: string | null;
  etd: string | null;
  ata: string | null;
  atd: string | null;
  freeTimeDays: number;
  /** Cont chưa trả vỏ (nhập) */
  containersOut: number;
  docs: { kind: string; status: string }[];
  /** Ngày thực tế các mốc đã có (mã mốc) */
  doneMilestones: string[];
  asiaReceiptRef: string | null;
  /** Ngày về kho (mốc delivered_wh) */
  deliveredOn: string | null;
  /** Số khoản thu / trả quá hạn chưa xong */
  overduePayments: number;
}

const DAY = 86_400_000;
const daysBetween = (from: string, to: string) => Math.round((Date.parse(`${to}T00:00:00Z`) - Date.parse(`${from}T00:00:00Z`)) / DAY);
const dmy = (d: string) => `${d.slice(8, 10)}/${d.slice(5, 7)}`;

/** Cảnh báo của 1 lô vào ngày `today` (YYYY-MM-DD). Lô đã đóng / hủy: không cảnh báo. */
export function shipmentAlerts(s: ShipmentAlertInput, today: string): TradeAlert[] {
  if (s.status === 'closed' || s.status === 'cancelled') return [];
  const out: TradeAlert[] = [];
  const done = new Set(s.doneMilestones);
  const arrived = !!s.ata || done.has('arrived');
  const departed = !!s.atd || done.has('departed');

  if (s.eta && s.etaBaseline && s.eta > s.etaBaseline && !arrived) {
    const n = daysBetween(s.etaBaseline, s.eta);
    out.push({ kind: 'eta_slip', level: n >= 7 ? 'error' : 'warning', text: `ETA lùi ${n} ngày (${dmy(s.etaBaseline)} → ${dmy(s.eta)})` });
  }
  if (s.direction === 'import' && s.eta && s.eta < today && !arrived)
    out.push({ kind: 'eta_overdue', level: 'error', text: `Quá ETA ${daysBetween(s.eta, today)} ngày, chưa ghi tàu đến` });

  // Thiếu chứng từ khi còn ≤ 7 ngày tới ETA (nhập) / ETD (xuất)
  const moveDate = s.direction === 'import' ? s.eta : s.etd;
  const moved = s.direction === 'import' ? arrived : departed;
  if (moveDate && !moved && daysBetween(today, moveDate) <= TRADE_DOCS_WARN_DAYS) {
    const missing = missingTradeDocs(s.direction, s.docs).filter((k) => TRADE_DOCS_BEFORE_MOVE[s.direction].includes(k));
    if (missing.length)
      out.push({
        kind: 'docs_missing',
        level: daysBetween(today, moveDate) <= 2 ? 'error' : 'warning',
        text: `${s.direction === 'import' ? 'Tàu đến' : 'Tàu chạy'} ${dmy(moveDate)}, thiếu ${missing.length} chứng từ`,
      });
  }

  // Free time cont: tính từ ngày tàu đến thực tế, còn cont chưa trả vỏ
  if (s.direction === 'import' && s.ata && s.containersOut > 0) {
    const ft = freeTimeStatus(s.ata, s.freeTimeDays, today);
    if (ft.daysLeft < 0)
      out.push({
        kind: 'free_time',
        level: 'error',
        text: `Quá free time ${-ft.daysLeft} ngày (hết ${dmy(ft.lastFreeDay)}), ${s.containersOut} cont chưa trả`,
      });
    else if (ft.daysLeft <= TRADE_FREE_TIME_WARN_DAYS)
      out.push({
        kind: 'free_time',
        level: 'warning',
        text: `Còn ${ft.daysLeft} ngày free time (hết ${dmy(ft.lastFreeDay)}), ${s.containersOut} cont chưa trả`,
      });
  }

  // Về kho mà chưa có số phiếu nhập Asia (cho 1 ngày để kế toán nhập)
  if (s.direction === 'import' && s.deliveredOn && !s.asiaReceiptRef && !done.has('asia_received') && daysBetween(s.deliveredOn, today) >= 1)
    out.push({ kind: 'not_in_asia', level: 'error', text: `Về kho ${dmy(s.deliveredOn)}, chưa nhập Asia` });

  if (s.overduePayments > 0)
    out.push({
      kind: 'payment_overdue',
      level: 'error',
      text: `${s.overduePayments} khoản ${s.direction === 'import' ? 'trả NCC' : 'thu tiền khách'} quá hạn`,
    });
  return out;
}

// ───────────────────────── Đối chiếu chứng từ (AI đọc) với lô ─────────────────────────

export interface DocLineRead {
  code?: string | null;
  name?: string | null;
  hsCode?: string | null;
  quantity?: number | null;
  unitPrice?: number | null;
  amount?: number | null;
  netKg?: number | null;
}
export interface ShipmentLineRef {
  id: number;
  productCode: string;
  productName: string;
  quantity: number;
  /** Đơn giá ngoại tệ theo đơn mua / lô */
  foreignUnitPrice: number | null;
}
export interface DocCompareIssue {
  level: 'warning' | 'error';
  lineId: number | null;
  text: string;
}
export interface DocCompareResult {
  /** dòng chứng từ i → id dòng lô (null = không khớp được) */
  matches: (number | null)[];
  issues: DocCompareIssue[];
}

const norm = (s: string | null | undefined) =>
  (s ?? '')
    .normalize('NFD')
    .replace(/[̀-ͯ]/g, '')
    .replace(/đ/gi, 'd')
    .toLowerCase()
    .replace(/[^a-z0-9]/g, '');

/** Lệch % của a so với chuẩn b (b = 0 → 0 khi a = 0, ngược lại 100). */
export function diffPct(a: number, b: number): number {
  if (b === 0) return a === 0 ? 0 : 100;
  return new Decimal(a).minus(b).abs().div(Math.abs(b)).mul(100).toDecimalPlaces(2, Decimal.ROUND_HALF_UP).toNumber();
}

/**
 * Ghép dòng chứng từ với dòng lô: theo mã hàng có trong mã / tên chứng từ, rồi theo tên, cuối cùng theo thứ tự khi số dòng bằng nhau.
 * Báo lệch SL, đơn giá, thành tiền > ±3%; dòng lô không có trên chứng từ; dòng chứng từ không khớp; tổng tiền lệch.
 */
export function compareTradeDocument(
  doc: { lines: DocLineRead[]; totalAmount?: number | null },
  lines: ShipmentLineRef[],
  expectedTotal: number | null,
  tolerancePct = TRADE_DOC_TOLERANCE_PCT,
): DocCompareResult {
  const issues: DocCompareIssue[] = [];
  const free = new Set(lines.map((l) => l.id));
  const matches: (number | null)[] = doc.lines.map(() => null);
  const take = (i: number, l: ShipmentLineRef | undefined) => {
    if (!l || !free.has(l.id)) return false;
    matches[i] = l.id;
    free.delete(l.id);
    return true;
  };
  doc.lines.forEach((d, i) => {
    const text = norm(`${d.code ?? ''} ${d.name ?? ''}`);
    const code = lines.find((l) => free.has(l.id) && norm(l.productCode).length >= 3 && text.includes(norm(l.productCode)));
    if (take(i, code)) return;
    const name = norm(d.name);
    if (name.length >= 4)
      take(
        i,
        lines.find((l) => free.has(l.id) && (norm(l.productName).includes(name) || name.includes(norm(l.productName)))),
      );
  });
  if (doc.lines.length === lines.length)
    doc.lines.forEach(
      (_, i) =>
        matches[i] === null &&
        take(
          i,
          lines.find((l) => free.has(l.id)),
        ),
    );

  doc.lines.forEach((d, i) => {
    const id = matches[i] ?? null;
    const label = d.code || d.name || `dòng ${i + 1}`;
    if (id === null) {
      issues.push({ level: 'error', lineId: null, text: `Chứng từ có "${label}" không có trên lô` });
      return;
    }
    const l = lines.find((x) => x.id === id)!;
    if (d.quantity != null && diffPct(d.quantity, l.quantity) > tolerancePct)
      issues.push({
        level: 'error',
        lineId: id,
        text: `${l.productCode}: SL chứng từ ${d.quantity} ≠ lô ${l.quantity} (lệch ${diffPct(d.quantity, l.quantity)}%)`,
      });
    if (d.unitPrice != null && l.foreignUnitPrice != null && diffPct(d.unitPrice, l.foreignUnitPrice) > 0.5)
      issues.push({ level: 'error', lineId: id, text: `${l.productCode}: đơn giá ${d.unitPrice} ≠ đơn mua ${l.foreignUnitPrice}` });
    if (d.amount != null && l.foreignUnitPrice != null) {
      const exp = new Decimal(l.foreignUnitPrice).mul(d.quantity ?? l.quantity).toNumber();
      if (diffPct(d.amount, exp) > tolerancePct) issues.push({ level: 'warning', lineId: id, text: `${l.productCode}: thành tiền ${d.amount} ≠ ${exp}` });
    }
  });
  for (const l of lines) if (free.has(l.id)) issues.push({ level: 'warning', lineId: l.id, text: `${l.productCode}: có trên lô, không thấy trên chứng từ` });
  if (doc.totalAmount != null && expectedTotal != null && expectedTotal > 0 && diffPct(doc.totalAmount, expectedTotal) > tolerancePct)
    issues.push({
      level: 'error',
      lineId: null,
      text: `Tổng tiền chứng từ ${doc.totalAmount} ≠ lô ${expectedTotal} (lệch ${diffPct(doc.totalAmount, expectedTotal)}%)`,
    });
  return { matches, issues };
}

// ───────────────────────── Hồ sơ VAT 0% lô xuất ─────────────────────────

export interface Vat0Item {
  key: 'contract' | 'customs' | 'invoice' | 'bank_payment';
  label: string;
  ok: boolean;
  hint: string;
}

/**
 * Điều kiện thuế suất VAT 0% hàng xuất khẩu (Luật Thuế GTGT 48/2024/QH15 Điều 9; NĐ 181/2025/NĐ-CP Điều 17 – ⚠ kế toán đối chiếu văn bản gốc):
 * hợp đồng bán với tổ chức nước ngoài, tờ khai hải quan xuất, chứng từ thanh toán qua ngân hàng; kèm hóa đơn / Commercial Invoice.
 */
export function vat0Checklist(input: {
  docs: { kind: string; status: string }[];
  customsDeclNo: string | null;
  /** Đã có khoản thu tiền khách ghi nhận đã nhận qua ngân hàng */
  bankReceived: boolean;
}): Vat0Item[] {
  const has = (k: string) => input.docs.some((d) => d.kind === k && d.status !== 'rejected');
  return [
    { key: 'contract', label: 'Hợp đồng bán / PI đã ký', ok: has('contract') || has('proforma'), hint: 'Tải hợp đồng / PI có chữ ký hai bên' },
    {
      key: 'customs',
      label: 'Tờ khai hải quan xuất',
      ok: has('customs_declaration') && !!input.customsDeclNo,
      hint: 'Ghi số tờ khai và tải tờ khai đã thông quan',
    },
    { key: 'invoice', label: 'Hóa đơn / Commercial Invoice', ok: has('commercial_invoice'), hint: 'Tải Invoice (hóa đơn GTGT xuất trên phần mềm hóa đơn)' },
    {
      key: 'bank_payment',
      label: 'Chứng từ thanh toán qua ngân hàng',
      ok: input.bankReceived && has('payment_proof'),
      hint: 'Ghi khoản thu đã nhận và tải giấy báo Có của ngân hàng',
    },
  ];
}

// ───────────────────────── In chứng từ lô xuất song ngữ ─────────────────────────

export const EXPORT_PRINT_DOCS = ['pi', 'invoice', 'packing'] as const;
export type ExportPrintDoc = (typeof EXPORT_PRINT_DOCS)[number];
export const EXPORT_PRINT_LANGS = ['en', 'zh'] as const;
export type ExportPrintLang = (typeof EXPORT_PRINT_LANGS)[number];

type L3 = { vi: string; en: string; zh: string };
export const EXPORT_PRINT_LABELS: Record<string, L3> = {
  pi: { vi: 'HÓA ĐƠN CHÀO HÀNG', en: 'PROFORMA INVOICE', zh: '形式发票' },
  invoice: { vi: 'HÓA ĐƠN THƯƠNG MẠI', en: 'COMMERCIAL INVOICE', zh: '商业发票' },
  packing: { vi: 'PHIẾU ĐÓNG GÓI', en: 'PACKING LIST', zh: '装箱单' },
  no: { vi: 'Số', en: 'No.', zh: '编号' },
  date: { vi: 'Ngày', en: 'Date', zh: '日期' },
  seller: { vi: 'Bên bán', en: 'Seller', zh: '卖方' },
  buyer: { vi: 'Bên mua', en: 'Buyer', zh: '买方' },
  item: { vi: 'Hàng hóa', en: 'Description', zh: '品名' },
  hs: { vi: 'Mã HS', en: 'HS code', zh: 'HS编码' },
  unit: { vi: 'ĐVT', en: 'Unit', zh: '单位' },
  qty: { vi: 'Số lượng', en: 'Quantity', zh: '数量' },
  price: { vi: 'Đơn giá', en: 'Unit price', zh: '单价' },
  amount: { vi: 'Thành tiền', en: 'Amount', zh: '金额' },
  netKg: { vi: 'TL tịnh (kg)', en: 'N.W. (kg)', zh: '净重 (kg)' },
  grossKg: { vi: 'TL cả bì (kg)', en: 'G.W. (kg)', zh: '毛重 (kg)' },
  packages: { vi: 'Số kiện', en: 'Packages', zh: '件数' },
  container: { vi: 'Cont / Seal', en: 'Container / Seal', zh: '集装箱 / 封号' },
  total: { vi: 'Tổng cộng', en: 'Total', zh: '合计' },
  incoterm: { vi: 'Điều kiện giao hàng', en: 'Terms of delivery', zh: '交货条件' },
  pol: { vi: 'Cảng xếp', en: 'Port of loading', zh: '装运港' },
  pod: { vi: 'Cảng dỡ', en: 'Port of discharge', zh: '目的港' },
  vessel: { vi: 'Tàu / chuyến', en: 'Vessel / Voyage', zh: '船名 / 航次' },
  payment: { vi: 'Thanh toán', en: 'Payment terms', zh: '付款条件' },
  bank: { vi: 'Ngân hàng hưởng', en: 'Beneficiary bank', zh: '收款银行' },
  origin: { vi: 'Xuất xứ', en: 'Country of origin', zh: '原产地' },
  sellerSign: { vi: 'Đại diện bên bán', en: 'For and on behalf of the Seller', zh: '卖方代表' },
  buyerSign: { vi: 'Đại diện bên mua', en: 'For and on behalf of the Buyer', zh: '买方代表' },
  validity: { vi: 'Hiệu lực chào giá', en: 'Validity', zh: '有效期' },
};

/** "Số / No." – nhãn tiếng Việt + ngôn ngữ khách */
export const bilingual = (key: string, lang: ExportPrintLang) => {
  const l = EXPORT_PRINT_LABELS[key];
  return l ? `${l.vi} / ${l[lang]}` : key;
};

/** Số chứng từ in từ mã lô: 001-LHX-281026 → PI-001-LHX-281026 / INV-… / PL-… */
export const exportDocNo = (doc: ExportPrintDoc, shipmentCode: string) => `${doc === 'pi' ? 'PI' : doc === 'invoice' ? 'INV' : 'PL'}-${shipmentCode}`;
